import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Minus } from 'lucide-react';
import { SEO } from '../seo/SEO';

const QUESTION_KEYS = [
  'appointment',
  'measurement',
  'duration',
  'fabric',
  'fitting',
  'alteration',
  'payment',
  'cancellation',
  'travel',
  'delivery',
] as const;

type QuestionKey = (typeof QUESTION_KEYS)[number];

export function FAQPage() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<QuestionKey | null>(null);

  function toggle(key: QuestionKey) {
    setOpen((current) => (current === key ? null : key));
  }

  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-20 sm:py-28">
      <SEO titleKey="seo.faq.title" descriptionKey="seo.faq.description" />
      <div className="grid grid-cols-12 gap-6 items-end mb-16">
        <div className="col-span-12 md:col-span-2 reveal reveal-delay-1">
          <span className="editorial-number text-6xl leading-none">N°</span>
          <div className="mt-3 editorial-label">{t('faq.kicker')}</div>
        </div>
        <div className="col-span-12 md:col-span-8 reveal reveal-delay-2">
          <h1 className="font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tight text-ink">
            <span className="block">{t('faq.titleLine1')}</span>
            <span className="block italic text-brass">{t('faq.titleLine2')}</span>
          </h1>
        </div>
        <div className="col-span-12 md:col-span-2 reveal reveal-delay-3">
          <div className="editorial-rule mb-4" />
          <p className="font-body text-sm text-ink-soft leading-relaxed">
            {t('faq.subtitle')}
          </p>
        </div>
      </div>

      <ul className="divide-y divide-linen border-y border-linen">
        {QUESTION_KEYS.map((key, idx) => {
          const isOpen = open === key;
          const num = String(idx + 1).padStart(2, '0');
          const panelId = `faq-panel-${key}`;
          const buttonId = `faq-button-${key}`;
          return (
            <li key={key}>
              <h2>
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggle(key)}
                  className="w-full flex items-center gap-6 py-6 text-start group"
                >
                  <span className="editorial-number text-2xl text-brass shrink-0 w-10">{num}</span>
                  <span className="flex-1 font-display text-xl sm:text-2xl text-ink group-hover:text-brass-dark transition-colors">
                    {t(`faq.items.${key}.question`)}
                  </span>
                  <span className="shrink-0 text-ink-soft" aria-hidden="true">
                    {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  </span>
                </button>
              </h2>
              {isOpen && (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className="ps-16 pe-12 pb-8 font-body text-base text-ink-soft leading-relaxed"
                >
                  {t(`faq.items.${key}.answer`)}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-16 pt-10 border-t border-linen flex flex-wrap items-center justify-between gap-4">
        <p className="font-display text-xl italic text-ink">{t('faq.cta.lead')}</p>
        <a
          href="mailto:hello@handcrafted.local"
          className="editorial-label text-ink hover:text-brass transition-colors"
        >
          → {t('faq.cta.button')}
        </a>
      </div>
    </section>
  );
}
